import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, addDoc, orderBy, where, limit, startAfter, DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Organization } from '../../types/organization';
import { User } from '../../types/user';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  BuildingOfficeIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserGroupIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import LoadingScreen from '../common/LoadingScreen';
import { formatDistanceToNow } from 'date-fns';
import { hu, enUS } from 'date-fns/locale';

interface OrganizationWithStats extends Organization {
  userCount: number;
  activeUserCount: number;
  lastActivity?: Date;
  subscriptionStatus?: 'active' | 'inactive' | 'trial' | 'expired';
  subscriptionPlan?: string;
}

const PAGE_SIZE = 20;

const SystemOrganizationManager: React.FC = () => {
  const { t, language } = useLanguage();
  const [organizations, setOrganizations] = useState<OrganizationWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastVisible, setLastVisible] = useState<DocumentData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  const [selectedOrg, setSelectedOrg] = useState<OrganizationWithStats | null>(null);
  const [orgStats, setOrgStats] = useState<{ userCount: number; activeUserCount: number; lastActivity?: Date } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    taxNumber: '',
    registrationNumber: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'Hungary'
    },
    contact: {
      name: '',
      email: '',
      phone: '',
      position: ''
    },
    isActive: true,
    subscriptionPlan: 'basic',
    subscriptionStatus: 'active' as 'active' | 'inactive' | 'trial' | 'expired'
  });

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchTerm]);

  // Fetch organizations with cursor pagination
  const fetchOrganizations = useCallback(async (reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
        setLastVisible(null);
      } else {
        setLoadingMore(true);
      }

      let q;
      const baseCollection = collection(db, 'organizations');

      if (debouncedSearch) {
        // Server-side search by name prefix
        const searchEnd = debouncedSearch + '\uf8ff';
        q = query(
          baseCollection,
          orderBy('name'),
          where('name', '>=', debouncedSearch),
          where('name', '<=', searchEnd),
          limit(PAGE_SIZE)
        );
      } else {
        // Default: most recent first
        q = query(
          baseCollection,
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
      }

      if (!reset && lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      const snapshot = await getDocs(q);
      const orgsData: OrganizationWithStats[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          userCount: 0,
          activeUserCount: 0,
          subscriptionStatus: data.subscriptionStatus || 'active',
          subscriptionPlan: data.subscriptionPlan || 'basic'
        } as OrganizationWithStats;
      });

      if (reset) {
        setOrganizations(orgsData);
      } else {
        setOrganizations(prev => [...prev, ...orgsData]);
      }

      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
      setTotalLoaded(prev => reset ? orgsData.length : prev + orgsData.length);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Hiba történt a szervezetek betöltésekor');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, lastVisible]);

  // Load on mount and when search changes
  useEffect(() => {
    fetchOrganizations(true);
  }, [debouncedSearch]);

  // Lazy load user stats for a single org
  const fetchOrgUserStats = async (orgId: string) => {
    setLoadingStats(true);
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', orgId)
      );
      const snapshot = await getDocs(usersQuery);
      const orgUsers = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as User[];
      const activeUsers = orgUsers.filter(u => (u as any).isActive !== false);
      
      const lastActivity = orgUsers.reduce((latest, user) => {
        const userLastLogin = (user as any).lastLoginAt?.toDate?.() || (user as any).lastLoginAt;
        if (userLastLogin && (!latest || userLastLogin > latest)) {
          return userLastLogin;
        }
        return latest;
      }, null as Date | null);

      const stats = {
        userCount: orgUsers.length,
        activeUserCount: activeUsers.length,
        lastActivity: lastActivity || undefined
      };
      setOrgStats(stats);

      // Update the org in the list too
      setOrganizations(prev => prev.map(o => 
        o.id === orgId ? { ...o, ...stats } : o
      ));
      return stats;
    } catch (error) {
      console.error('Error fetching org user stats:', error);
      return null;
    } finally {
      setLoadingStats(false);
    }
  };

  // Client-side filters (status & subscription) on loaded results
  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org => {
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && org.isActive) ||
        (statusFilter === 'inactive' && !org.isActive);
      const matchesSubscription = subscriptionFilter === 'all' || 
        org.subscriptionStatus === subscriptionFilter;
      return matchesStatus && matchesSubscription;
    });
  }, [organizations, statusFilter, subscriptionFilter]);

  const handleView = (org: OrganizationWithStats) => {
    setSelectedOrg(org);
    setOrgStats(null);
    setIsViewModalOpen(true);
    fetchOrgUserStats(org.id);
  };

  const handleEdit = (org: OrganizationWithStats) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      taxNumber: org.taxNumber || '',
      registrationNumber: org.registrationNumber || '',
      address: org.address,
      contact: org.contact,
      isActive: org.isActive,
      subscriptionPlan: org.subscriptionPlan || 'basic',
      subscriptionStatus: org.subscriptionStatus || 'active'
    });
    setIsEditModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedOrg(null);
    setFormData({
      name: '',
      taxNumber: '',
      registrationNumber: '',
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'Hungary'
      },
      contact: {
        name: '',
        email: '',
        phone: '',
        position: ''
      },
      isActive: true,
      subscriptionPlan: 'basic',
      subscriptionStatus: 'active'
    });
    setIsCreateModalOpen(true);
  };

  const handleDelete = async (org: OrganizationWithStats) => {
    setSelectedOrg(org);
    setIsDeleteModalOpen(true);
    // Lazy load user count for delete check
    if (org.userCount === 0) {
      const stats = await fetchOrgUserStats(org.id);
      if (stats && stats.userCount > 0) {
        setSelectedOrg({ ...org, ...stats });
      }
    }
  };

  const confirmDelete = async () => {
    if (!selectedOrg) return;
    
    try {
      // Check if organization has users
      if (selectedOrg.userCount > 0) {
        toast.error('Nem törölhető olyan szervezet, amelyhez felhasználók tartoznak');
        return;
      }
      
      await deleteDoc(doc(db, 'organizations', selectedOrg.id));
      toast.success('Szervezet sikeresen törölve');
      setIsDeleteModalOpen(false);
      setSelectedOrg(null);
      fetchOrganizations(true);
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error('Hiba történt a szervezet törlésekor');
    }
  };

  const handleSave = async () => {
    try {
      if (selectedOrg) {
        // Update existing organization
        const orgRef = doc(db, 'organizations', selectedOrg.id);
        await updateDoc(orgRef, {
          ...formData,
          updatedAt: new Date()
        });
        toast.success('Szervezet sikeresen frissítve');
        setIsEditModalOpen(false);
      } else {
        // Create new organization
        await addDoc(collection(db, 'organizations'), {
          ...formData,
          ownerId: '', // Will be set when first user joins
          users: [],
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('Szervezet sikeresen létrehozva');
        setIsCreateModalOpen(false);
      }
      
      setSelectedOrg(null);
      fetchOrganizations(true);
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error('Hiba történt a szervezet mentésekor');
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
        <CheckCircleIcon className="w-3 h-3 mr-1" />
        Aktív
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
        <XCircleIcon className="w-3 h-3 mr-1" />
        Inaktív
      </span>
    );
  };

  const getSubscriptionBadge = (status: string) => {
    const badges = {
      active: { color: 'green', text: 'Aktív előfizetés' },
      trial: { color: 'blue', text: 'Próbaidőszak' },
      expired: { color: 'red', text: 'Lejárt' },
      inactive: { color: 'gray', text: 'Inaktív' }
    };
    
    const badge = badges[status as keyof typeof badges] || badges.inactive;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-100 dark:bg-${badge.color}-900/30 text-${badge.color}-800 dark:text-${badge.color}-300`}>
        {badge.text}
      </span>
    );
  };

  const formatDate = (date: Date) => {
    const formatted = formatDistanceToNow(date, {
      addSuffix: true,
      locale: language === 'hu' ? hu : enUS
    });
    
    if (language === 'hu') {
      return formatted
        .replace('körülbelül ', '')
        .replace(' ezelőtt', '')
        .replace('órával', 'órája')
        .replace('nappal', 'napja')
        .replace('héttel', 'hete')
        .replace('hónappal', 'hónapja')
        .replace('évvel', 'éve')
        .replace('perccel', 'perce')
        .replace('másodperccel', 'másodperce');
    }
    
    return formatted;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Szervezetek kezelése
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Betöltve: {totalLoaded} szervezet{hasMore ? ' (több is van)' : ''}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Új szervezet
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Keresés szervezet neve szerint..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>
          
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">Minden státusz</option>
              <option value="active">Aktív</option>
              <option value="inactive">Inaktív</option>
            </select>
          </div>
          
          <div>
            <select
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">Minden előfizetés</option>
              <option value="active">Aktív előfizetés</option>
              <option value="trial">Próbaidőszak</option>
              <option value="expired">Lejárt</option>
              <option value="inactive">Inaktív</option>
            </select>
          </div>
          
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <FunnelIcon className="w-4 h-4 mr-1" />
            {filteredOrganizations.length} találat
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Szervezet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Kapcsolat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Felhasználók
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Előfizetés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Utolsó aktivitás
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {org.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {org.taxNumber && `Adószám: ${org.taxNumber}`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {org.contact.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {org.contact.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                      <UserGroupIcon className="w-4 h-4 mr-1 text-gray-400" />
                      {org.userCount > 0 ? `${org.activeUserCount}/${org.userCount}` : '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {getSubscriptionBadge(org.subscriptionStatus || 'active')}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {org.subscriptionPlan || 'basic'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(org.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {org.lastActivity ? (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        {formatDate(org.lastActivity)}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleView(org)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(org)}
                        className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(org)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Load More */}
        {hasMore && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
            <button
              onClick={() => fetchOrganizations(false)}
              disabled={loadingMore}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                  Betöltés...
                </>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Több betöltése
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Szervezet részletei"
        size="lg"
      >
        {selectedOrg && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Alapadatok
                </h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Név</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{selectedOrg.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Adószám</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{selectedOrg.taxNumber || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Cégjegyzékszám</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{selectedOrg.registrationNumber || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Státusz</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">
                      {getStatusBadge(selectedOrg.isActive)}
                    </dd>
                  </div>
                </dl>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Kapcsolattartó
                </h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Név</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{selectedOrg.contact.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{selectedOrg.contact.email}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Telefon</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{selectedOrg.contact.phone || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Pozíció</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{selectedOrg.contact.position || '-'}</dd>
                  </div>
                </dl>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Cím
              </h3>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {selectedOrg.address.street}<br />
                {selectedOrg.address.postalCode} {selectedOrg.address.city}<br />
                {selectedOrg.address.country}
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Statisztikák
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {loadingStats ? '...' : (orgStats?.userCount ?? selectedOrg.userCount)}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Összes felhasználó
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {loadingStats ? '...' : (orgStats?.activeUserCount ?? selectedOrg.activeUserCount)}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Aktív felhasználó
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedOrg.subscriptionPlan || 'basic'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Előfizetési csomag
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {loadingStats ? '...' : ((orgStats?.lastActivity || selectedOrg.lastActivity) ? formatDate(orgStats?.lastActivity || selectedOrg.lastActivity!) : 'Nincs adat')}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Utolsó aktivitás
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isCreateModalOpen || isEditModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
        }}
        title={selectedOrg ? 'Szervezet szerkesztése' : 'Új szervezet létrehozása'}
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Szervezet neve *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Adószám
              </label>
              <input
                type="text"
                value={formData.taxNumber}
                onChange={(e) => setFormData({ ...formData, taxNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kapcsolattartó neve *
              </label>
              <input
                type="text"
                value={formData.contact.name}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  contact: { ...formData.contact, name: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email cím *
              </label>
              <input
                type="email"
                value={formData.contact.email}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  contact: { ...formData.contact, email: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Előfizetési csomag
              </label>
              <select
                value={formData.subscriptionPlan}
                onChange={(e) => setFormData({ ...formData, subscriptionPlan: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="basic">Alap</option>
                <option value="professional">Professzionális</option>
                <option value="enterprise">Vállalati</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Előfizetés státusza
              </label>
              <select
                value={formData.subscriptionStatus}
                onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="active">Aktív</option>
                <option value="trial">Próbaidőszak</option>
                <option value="expired">Lejárt</option>
                <option value="inactive">Inaktív</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
              Aktív szervezet
            </label>
          </div>
          
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Mégse
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {selectedOrg ? 'Frissítés' : 'Létrehozás'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Szervezet törlése"
      >
        {selectedOrg && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Biztosan törölni szeretnéd a következő szervezetet?
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {selectedOrg.name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {selectedOrg.contact.email}
              </p>
            </div>
            {selectedOrg.userCount > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-600 dark:text-red-400">
                  Ez a szervezet nem törölhető, mert {selectedOrg.userCount} felhasználó tartozik hozzá.
                  Először távolítsd el vagy helyezd át a felhasználókat.
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={selectedOrg.userCount > 0}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Törlés
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SystemOrganizationManager;
